import { FileAttachment } from '@/types/model/inspection-modal';

export interface PlanningAttachmentRecord {
  Id?: number;
  Description: string;
  Link?: string | null;
  FileId?: number | null;
  Filename?: string | null;
}

export interface GroupFormValues {
  identifier: string;
  title: string;
}

export interface LineItemFormValues {
  title: string;
  groupId: number;
  paragraph: string;
  subParagraph: string;
}

export interface ChecklistValidationItem {
  text: string;
  weight: number;
}

export interface FrequencyValidationLevel {
  level: 'normal' | 'decreased' | 'increased';
  frequencyId: number;
  low?: number;
  high?: number;
}

export interface InspectionPlanValidationValues {
  checklistItems: ChecklistValidationItem[];
  frequencyMode: 'auto' | 'fixed' | 'profile';
  frequencyLevels: FrequencyValidationLevel[];
  expertiseAreaId: number;
  primaryValue: string;
  alternateValue: string;
  fixedInspectionDateCount: number;
}

export const mapPlanningAttachment = (
  attachment: PlanningAttachmentRecord,
  index: number,
): FileAttachment => ({
  type: attachment.Link ? 'link' : attachment.FileId ? 'existing-resource' : 'local-file',
  index,
  attachmentId: attachment.Id,
  fileId: attachment.FileId ? attachment.FileId.toString() : undefined,
  description: attachment.Description || '',
  link: attachment.Link || '',
  fileName: attachment.Filename || undefined,
});

export const mapPlanningAttachments = (
  attachments: PlanningAttachmentRecord[] | null | undefined,
): FileAttachment[] => (attachments ?? []).map(mapPlanningAttachment);

export const buildPlanningLineItemIdentifier = (
  groupIdentifier: string,
  paragraph?: string,
  subParagraph?: string,
  remainder?: string,
): string => [groupIdentifier, paragraph, subParagraph, remainder]
  .map(value => value === undefined || value === null ? '' : String(value).trim())
  .filter((value): value is string => Boolean(value))
  .join('.');

export const formatPlanningDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-CA');

export const findPlanningFrequencyId = (
  frequencies: Array<{ Id: number; Name: string }>,
  name: string,
): number => frequencies.find(frequency => frequency.Name.toLowerCase() === name.toLowerCase())?.Id ?? 0;

export const validateGroupForm = (formData: GroupFormValues): { identifier?: string; title?: string } => {
  const errors: { identifier?: string; title?: string } = {};
  const identifier = formData.identifier.trim();

  if (!identifier) {
    errors.identifier = 'Please enter a group number.';
  } else if (!/^\d+$/.test(identifier)) {
    errors.identifier = 'Group number must be a valid whole number.';
  } else if (Number.parseInt(identifier, 10) < 1) {
    errors.identifier = 'Group number must be 1 or greater.';
  }

  if (!formData.title.trim()) {
    errors.title = 'Please enter a title.';
  }

  return errors;
};

export const validateLineItemForm = (formData: LineItemFormValues): string[] => {
  const errors: string[] = [];

  if (!formData.title.trim()) {
    errors.push('Please enter a title.');
  } else if (formData.title.trim().length > 150) {
    errors.push('Title cannot exceed 150 characters.');
  }

  if (formData.groupId === 0) {
    errors.push('Please select a group.');
  }

  if (!formData.paragraph.trim()) {
    errors.push('Please enter a paragraph.');
  }

  if (formData.subParagraph.trim() && !/^\d+$/.test(formData.subParagraph.trim())) {
    errors.push('Sub-paragraph must be a whole number.');
  }

  return errors;
};

export const validateInspectionPlanForm = ({
  checklistItems,
  frequencyMode,
  frequencyLevels,
}: Pick<InspectionPlanValidationValues, 'checklistItems' | 'frequencyMode' | 'frequencyLevels'>): string[] => {
  const errors: string[] = [];

  checklistItems.forEach((item, index) => {
    if (!item.text.trim()) {
      errors.push(`Checklist item ${index + 1}: please enter a checklist question.`);
    }
    if (item.weight <= 0) {
      errors.push(`Checklist item ${index + 1}: checklist weight must be greater than 0.`);
    }
  });

  if (frequencyMode === 'auto' && frequencyLevels.some(level => level.frequencyId < 1)) {
    errors.push('Please select all required auto-adjusted frequencies.');
  }

  return errors;
};

export const validateActiveInspectionPlanRules = ({
  checklistItems,
  frequencyMode,
  frequencyLevels,
  expertiseAreaId,
  primaryValue,
  alternateValue,
  fixedInspectionDateCount,
}: InspectionPlanValidationValues): string[] => {
  const errors: string[] = [];
  const primary = Number.parseFloat(primaryValue) || 0;
  const alternate = Number.parseFloat(alternateValue) || 0;

  if (expertiseAreaId < 1) {
    errors.push('Please select an area of expertise before activating this plan.');
  }
  if (primary <= 0 && alternate <= 0) {
    errors.push('At least one of primary weight or alternate weight must be greater than 0.');
  }

  if (frequencyMode === 'auto') {
    const normal = frequencyLevels.find(level => level.level === 'normal');
    if (frequencyLevels.some(level => level.frequencyId < 1)) {
      errors.push('Please select frequencies for Normal, Decreased, and Increased levels.');
    }
    if (normal?.low === undefined || normal.high === undefined || normal.low >= normal.high) {
      errors.push('For the Normal level, Low must be less than High.');
    }
  }

  if (frequencyMode === 'fixed' && fixedInspectionDateCount === 0) {
    errors.push('Please add at least one fixed inspection date.');
  }
  if (checklistItems.length === 0) {
    errors.push('Please add at least one checklist item.');
  }

  return errors;
};
